import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Confetti component
const Confetti = () => {
  const [confetti, setConfetti] = useState([]);

  useEffect(() => {
    const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
    const newConfetti = [];
    
    for (let i = 0; i < 80; i++) {
      newConfetti.push({
        id: i,
        x: Math.random() * 100,
        y: -20 - Math.random() * 50,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 12 + 6,
        speed: Math.random() * 4 + 3,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 15,
        delay: Math.random() * 3000,
        sway: Math.random() * 2 - 1,
        swaySpeed: Math.random() * 0.02 + 0.01
      });
    }
    
    setConfetti(newConfetti);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setConfetti(prev => 
        prev.map(piece => ({
          ...piece,
          y: piece.y + piece.speed,
          x: piece.x + Math.sin(piece.y * piece.swaySpeed) * piece.sway,
          rotation: piece.rotation + piece.rotationSpeed
        }))
      );
    }, 30);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-10 overflow-hidden">
      {confetti.map(piece => (
        <div
          key={piece.id}
          className="absolute"
          style={{
            left: `${piece.x}%`,
            top: `${piece.y}%`,
            transform: `rotate(${piece.rotation}deg)`,
            animationDelay: `${piece.delay}ms`,
            animationDuration: `${Math.random() * 3 + 2}s`
          }}
        >
          <div
            className="rounded-sm shadow-sm"
            style={{
              width: `${piece.size}px`,
              height: `${piece.size}px`,
              backgroundColor: piece.color,
              opacity: piece.y > 120 ? 0 : Math.max(0, 1 - piece.y / 120),
              transition: 'opacity 0.3s ease-out',
              boxShadow: `0 2px 4px rgba(0,0,0,0.1)`
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default function FinalStep({ onComplete, data }) {
  const { currentUser } = useAuth();
  const displayName = currentUser?.displayName || 'there';
  const [showConfetti, setShowConfetti] = useState(false);
  const navigate = useNavigate();

  const handleGoToCrypto = () => {
    // Navigate to the main crypto market (Home component)
    onComplete();
  };

  const handleGoToDashboard = () => {
    // Navigate to the dashboard view specifically
    onComplete();
    // Set a flag to show dashboard instead of crypto market
    localStorage.setItem('showDashboard', 'true');
  };

  const checklistItems = [
    {
      id: 'crypto-market',
      title: 'Top 500 Crypto Market Ready',
      description: 'Real-time prices and market data for 500+ cryptocurrencies',
      icon: '📈'
    },
    {
      id: 'ai-analysis',
      title: 'AI-Powered Analysis Active',
      description: 'Advanced technical indicators and market insights',
      icon: '🤖'
    },
    {
      id: 'personalized',
      title: 'Personalized Experience',
      description: 'Tailored to your experience level and investment goals',
      icon: '⚡'
    }
  ];

  useEffect(() => {
    // Trigger confetti after a short delay
    const timer = setTimeout(() => {
      setShowConfetti(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);



  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Background grid pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
          backgroundSize: '20px 20px'
        }} />
      </div>

      {/* Confetti */}
      {showConfetti && <Confetti />}

      {/* Floating geometric shapes */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-3 h-3 bg-green-400 rounded-full opacity-30 floating-shape blur-sm" style={{ animationDelay: '0s' }} />
        <div className="absolute top-32 right-20 w-2 h-2 bg-yellow-400 rounded-full opacity-30 floating-shape blur-sm" style={{ animationDelay: '1s' }} />
        <div className="absolute top-40 left-1/4 w-4 h-4 bg-purple-400 rounded-full opacity-30 floating-shape blur-sm" style={{ animationDelay: '2s' }} />
        <div className="absolute top-60 right-1/3 w-2 h-2 bg-blue-400 rounded-full opacity-30 floating-shape blur-sm" style={{ animationDelay: '0.5s' }} />
        <div className="absolute top-80 left-1/3 w-3 h-3 bg-pink-400 rounded-full opacity-20 floating-shape blur-sm" style={{ animationDelay: '1.5s' }} />
        <div className="absolute top-40 right-1/4 w-2 h-2 bg-cyan-400 rounded-full opacity-25 floating-shape blur-sm" style={{ animationDelay: '0.8s' }} />
      </div>

      <div className="relative z-20 max-w-2xl mx-auto px-4 pt-4 text-center animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
        {/* Brand Icon */}
        <div className="mb-6 mt-2">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 shadow-2xl shadow-blue-500/25">
            <span className="text-lg font-bold text-white">CI</span>
          </div>
          <h2 className="text-lg font-semibold text-white mb-2">
            CryptoIndicators.AI
          </h2>
        </div>

        {/* Main Success Message */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
            You're all set!
          </h1>
          <p className="text-base text-gray-300 leading-relaxed">
            Your dashboard is now tailored for your experience and portfolio, {displayName}.
          </p>
        </div>

        {/* Large Success Checkmark */}
        <div className="mb-6">
          <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto shadow-2xl shadow-green-500/25 relative success-checkmark">
            {/* Inner glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-green-600 rounded-full opacity-50 blur-xl"></div>
            <div className="relative z-10">
              <svg className="w-10 h-10 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Feature Checklist */}
        <div className="mb-6 space-y-3">
          {checklistItems.map((item, index) => (
            <div 
              key={item.id} 
              className="flex items-center justify-center space-x-4 p-4 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 animate-fade-in-up"
              style={{ animationDelay: `${index * 200}ms` }}
            >
              <div className="text-2xl">{item.icon}</div>
              <div className="text-left flex-1">
                <h3 className="font-semibold text-white text-lg">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.description}</p>
              </div>
              <div className="text-green-400">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action Buttons */}
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            {/* Explore Crypto Market Button */}
            <button
              onClick={handleGoToCrypto}
              className="group relative px-6 py-3 bg-gradient-to-r from-slate-600 to-slate-700 text-white font-semibold rounded-xl hover:from-slate-700 hover:to-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500/50 transition-all duration-300 text-base shadow-lg shadow-slate-500/25 transform hover:scale-105 active:scale-95 overflow-hidden w-full sm:w-auto"
            >
              {/* Button shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              <span className="relative z-10 flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                View Top 500 Crypto Market
              </span>
            </button>

            {/* Dashboard Button */}
            <button
              onClick={handleGoToDashboard}
              className="group relative px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-300 text-base shadow-lg shadow-blue-500/25 transform hover:scale-105 active:scale-95 overflow-hidden w-full sm:w-auto"
            >
              {/* Button shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              <span className="relative z-10 flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Go to Dashboard
              </span>
            </button>
          </div>
        </div>

        {/* Footer Note */}
        <p className="text-xs text-gray-500">
          Choose your next step - explore our top 500 cryptocurrency market with AI-powered analysis or go to your personalized dashboard.
        </p>
      </div>
    </div>
  );
} 